<script setup lang="ts">
import { ArrowDown, ArrowUp } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import UploadedFileActionMenu from '@/components/files/UploadedFileActionMenu.vue';
import type { UploadedFile } from '~/types/models';

const props = withDefaults(defineProps<{
    files: UploadedFile[];
    busyFileId?: string;
    showControls?: boolean;
    loading?: boolean;
    emptyText?: string;
    variant?: 'full' | 'compact';
    scrollable?: boolean;
    batchSize?: number;
    layout?: 'one' | 'two' | 'three' | 'four' | 'masonry';
    showReorderControls?: boolean;
    projectOptions?: Array<{ value: string, label: string }>;
    allowAddToProject?: boolean;
    allowSetCover?: boolean;
    allowTogglePortalVisibility?: boolean;
    allowEdit?: boolean;
    allowDelete?: boolean;
    selectable?: boolean;
    selectedFileIds?: string[];
}>(), {
    busyFileId: '',
    showControls: false,
    loading: false,
    emptyText: 'No gallery images yet.',
    variant: 'full',
    scrollable: false,
    batchSize: 12,
    layout: 'two',
    showReorderControls: true,
    projectOptions: () => [],
    allowAddToProject: false,
    allowSetCover: true,
    allowTogglePortalVisibility: true,
    allowEdit: true,
    allowDelete: true,
    selectable: false,
    selectedFileIds: () => [],
});

const emit = defineEmits<{
    (e: 'move', payload: { file: UploadedFile, direction: -1 | 1 }): void;
    (e: 'toggle-portal-visibility', file: UploadedFile): void;
    (e: 'set-cover', file: UploadedFile): void;
    (e: 'edit', file: UploadedFile): void;
    (e: 'remove', file: UploadedFile): void;
    (e: 'add-to-project', payload: { file: UploadedFile, projectId: string }): void;
    (e: 'toggle-select', fileId: string): void;
}>();

const isPreviewOpen = ref(false);
const previewIndex = ref(0);
const previewFile = computed(() => props.files[previewIndex.value] || null);
const visibleCount = ref(props.batchSize);
const visibleFiles = computed(() => props.scrollable ? props.files.slice(0, visibleCount.value) : props.files);
const hasMore = computed(() => props.scrollable && visibleFiles.value.length < props.files.length);

const gridClass = computed(() => {
    if (props.layout === 'masonry') return 'columns-1 gap-4 sm:columns-2 xl:columns-3';
    const columns = {
        one: 'grid-cols-1',
        two: 'grid-cols-1 sm:grid-cols-2',
        three: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
        four: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4',
    }[props.layout];
    return `grid ${props.variant === 'compact' ? 'gap-4' : 'gap-5'} ${columns}`;
});

const imageFrameClass = computed(() => {
    if (props.layout === 'masonry') return 'relative';
    if (props.variant === 'full') return 'relative h-[26rem]';
    return {
        one: 'relative aspect-[16/9]',
        two: 'relative aspect-[4/3]',
        three: 'relative aspect-square',
        four: 'relative aspect-square',
    }[props.layout];
});

const imageButtonClass = computed(() => props.layout === 'masonry'
    ? 'block w-full overflow-hidden bg-muted/40 text-left'
    : 'block h-full w-full overflow-hidden bg-muted/40 text-left');

const imageClass = computed(() => props.layout === 'masonry'
    ? 'h-auto w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]'
    : 'h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]');

const articleClass = computed(() => `${props.variant === 'compact'
    ? 'group overflow-hidden rounded-2xl border border-border/60 bg-background/75 shadow-sm'
    : 'group overflow-hidden rounded-2xl border bg-card shadow-sm'}${props.layout === 'masonry' ? ' mb-4 break-inside-avoid' : ''}`);

function openPreview(index: number) {
    previewIndex.value = index;
    isPreviewOpen.value = true;
}

function showPrevious() {
    if (props.files.length === 0) return;
    previewIndex.value = previewIndex.value === 0 ? props.files.length - 1 : previewIndex.value - 1;
}

function showNext() {
    if (props.files.length === 0) return;
    previewIndex.value = previewIndex.value === props.files.length - 1 ? 0 : previewIndex.value + 1;
}

watch(() => props.files.length, (length) => {
    if (length < visibleCount.value) {
        visibleCount.value = Math.max(props.batchSize, length);
    }
});

function revealNextBatch() {
    visibleCount.value = Math.min(props.files.length, visibleCount.value + props.batchSize);
}

function handleScroll(event: Event) {
    if (!hasMore.value) return;
    const target = event.currentTarget as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 240) revealNextBatch();
}
</script>

<template>
    <div v-if="loading && files.length === 0" :class="gridClass" aria-label="Loading gallery">
        <Skeleton v-for="index in 4" :key="index" class="h-52 w-full rounded-2xl" />
    </div>

    <div v-else-if="files.length === 0" class="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
        {{ emptyText }}
    </div>

    <div
        v-else
        :class="scrollable ? 'max-h-[40rem] overflow-y-auto overscroll-contain pr-1' : ''"
        :tabindex="scrollable ? 0 : undefined"
        aria-label="Image gallery"
        @scroll.passive="handleScroll"
    >
        <div :class="gridClass">
            <article
                v-for="(file, index) in visibleFiles"
                :key="file.id"
                :class="[articleClass, selectedFileIds.includes(file.id) ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : '']"
            >
                <div :class="imageFrameClass">
                    <button
                        type="button"
                        :class="imageButtonClass"
                        @click="openPreview(index)"
                    >
                        <img
                            :src="file.fileURL"
                            :alt="file.galleryAltText || file.galleryCaption || file.fileName"
                            loading="lazy"
                            decoding="async"
                            :class="imageClass"
                        >
                    </button>

                <div class="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-3">
                    <div class="flex items-center gap-2">
                        <Checkbox
                            v-if="selectable"
                            :model-value="selectedFileIds.includes(file.id)"
                            class="border-white/70 bg-background/90"
                            :aria-label="`Select ${file.fileName}`"
                            @click.stop
                            @update:model-value="emit('toggle-select', file.id)"
                        />
                        <Badge v-if="file.isProjectCover" variant="default">Cover</Badge>
                        <Badge variant="outline" class="bg-background/90">{{ index + 1 }}</Badge>
                    </div>

                    <div v-if="showControls" class="flex items-center gap-1 rounded-full bg-background/90 p-1 shadow-sm">
                        <Button
                            v-if="showReorderControls"
                            size="icon-sm"
                            variant="ghost"
                            class="rounded-full"
                            :disabled="index === 0 || busyFileId === file.id"
                            @click="emit('move', { file, direction: -1 })"
                        >
                            <ArrowUp class="size-4" />
                            <span class="sr-only">Move up</span>
                        </Button>
                        <Button
                            v-if="showReorderControls"
                            size="icon-sm"
                            variant="ghost"
                            class="rounded-full"
                            :disabled="index === files.length - 1 || busyFileId === file.id"
                            @click="emit('move', { file, direction: 1 })"
                        >
                            <ArrowDown class="size-4" />
                            <span class="sr-only">Move down</span>
                        </Button>
                        <UploadedFileActionMenu
                            :file="file"
                            :project-options="projectOptions"
                            :busy="busyFileId === file.id"
                            :allow-add-to-project="allowAddToProject"
                            :allow-set-cover="allowSetCover"
                            :allow-toggle-portal-visibility="allowTogglePortalVisibility"
                            :allow-edit="allowEdit"
                            :allow-delete="allowDelete"
                            @add-to-project="emit('add-to-project', $event)"
                            @set-cover="emit('set-cover', $event)"
                            @toggle-portal-visibility="emit('toggle-portal-visibility', $event)"
                            @edit="emit('edit', $event)"
                            @delete="emit('remove', $event)"
                        />
                    </div>
                </div>

                <div
                    v-if="file.galleryCaption || file.fileName"
                    class="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-4 pb-4 pt-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                >
                    <p class="line-clamp-2 text-sm font-medium text-white">
                        {{ file.galleryCaption || file.fileName }}
                    </p>
                </div>
                </div>
            </article>
        </div>

        <div v-if="hasMore" class="sticky bottom-0 flex justify-center bg-background/95 py-3 backdrop-blur">
            <Button type="button" variant="ghost" size="sm" @click="revealNextBatch">
                Load more images
            </Button>
        </div>
    </div>

    <Dialog :open="isPreviewOpen" @update:open="(value) => isPreviewOpen = value">
        <DialogContent class="w-[96vw] max-w-6xl border-none bg-black/95 p-0 text-white" :show-close-button="false" :mobile-floating="false">
            <DialogHeader class="sr-only">
                <DialogTitle>Gallery preview</DialogTitle>
            </DialogHeader>

            <div v-if="previewFile" class="space-y-4 p-4 sm:p-6">
                <img :src="previewFile.fileURL" :alt="previewFile.galleryAltText || previewFile.galleryCaption || previewFile.fileName" class="max-h-[70vh] w-full rounded-2xl object-contain">

                <div class="flex flex-wrap items-center justify-between gap-3">
                    <div class="space-y-1">
                        <p class="text-sm font-medium text-white">{{ previewFile.galleryCaption || previewFile.fileName }}</p>
                        <p class="text-xs text-white/70">{{ previewIndex + 1 }} / {{ files.length }}</p>
                    </div>
                    <div class="flex items-center gap-2">
                        <Button variant="ghost" size="sm" class="text-white hover:bg-white/10 hover:text-white" @click="showPrevious">Previous</Button>
                        <Button variant="ghost" size="sm" class="text-white hover:bg-white/10 hover:text-white" @click="showNext">Next</Button>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>
